/**
 * BulkEmailModal
 *
 * A reusable three-step email modal that uses the SendIt MFE in **bulk** mode.
 *
 * Step 1 – Compose: template picker, subject, message body, attach invoices toggle, file upload.
 * Step 2 – Recipients: tabular view with checkbox, Account, Customer, To, CC columns.
 * Step 3 – SendIt MFE: embedded MFE in bulk mode.
 *
 * Callers provide recipient data & a `buildMergeData` callback so the modal
 * stays domain-agnostic.
 */

import { EvoMfeApp } from '@access-evo/evo-api';
import Placeholder from '@tiptap/extension-placeholder';
import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ArrowRight, Check, ChevronLeft, Loader2, Mail, Paperclip, Plus, User, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ApiError } from '@/hooks/useApi';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import { useTemplateById, useTemplatesList } from '@/hooks/useTemplatesData';
import {
  convertVariableNodesToPlaceholders,
  convertVariableNodesToText,
  convertVariablesToNodes,
  disallowedSubjectVariablesMessage,
  isValidEmail,
  lowercaseMergeKeys,
  mergeDataPlaceholderMessage,
  type UploadedFile,
} from '@/lib/email-placeholder-utils';
import { contactDisplayName } from '@/lib/contact-display-name';
import { VariableExtension } from '@/lib/tiptap-variable-extension';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useMode } from '@/providers/ModeProvider';
import type { ContactUI } from '@/types/ui/customer-detail';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BulkEmailRecipient {
  id: string;
  name: string;
  accountNumber: string;
  defaultEmail: string;
}

export interface MergeDataResult {
  mergeData: Record<string, string>;
  /** Fields that should be injected as raw HTML (not escaped by the MFE). */
  mergeDataHtml?: Record<string, string>;
}

export interface BulkEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown in the dialog header (defaults to "Send Email") */
  title?: string;
  /** List of recipients for bulk mode */
  recipients: BulkEmailRecipient[];
  /** Build per-recipient merge data for placeholder resolution */
  buildMergeData: (recipient: BulkEmailRecipient, toEmail: string) => MergeDataResult;
  /** Callback event name the MFE fires on completion (defaults to "creditcontrol:bulk-email-done") */
  callbackEventName?: string;
  /** Attach invoices checkbox label (defaults to "Attach outstanding invoices") */
  attachLabel?: string;
  /** Loading text shown while preparing attachments (defaults to "Loading Customer Invoices") */
  preparingLabel?: string;
  /** When true the attach-invoices checkbox starts checked (defaults to false) */
  defaultAttachChecked?: boolean;
  /** Optional per-recipient attachment fetcher. When provided, checking the attach
   *  checkbox enables it and attachments are generated just before the MFE opens. */
  fetchAttachments?: (recipient: BulkEmailRecipient) => Promise<UploadedFile[]>;
  /** Called when the MFE reports success */
  onSuccess?: () => void;
  /**
   * Optional override for the step indicator text shown in each step's subtitle.
   * When provided, replaces the default "Step N of 3" with "Step (current + offset) of total".
   * Pass `{ startAt: 2, total: 4 }` from the Send Statements flow so the modal
   * shows "Step 2 of 4", "Step 3 of 4", "Step 4 of 4" instead of "Step 1–3 of 3".
   */
  stepOverride?: { startAt: number; total: number };
  /** Recipients dropped by the caller before this step, shown alongside the recipient count */
  excludedRecipientCount?: number;
  /**
   * Optional async callback to fetch merge data at Step 2 "Next" click time.
   * When provided, the merge data is fetched with the composed subject/body
   * so the backend can selectively resolve only the required fields.
   * While fetching, the Step 2 button shows "Preparing emails\u2026".
   */
  onFetchMergeData?: (
    subject: string,
    body: string,
    templateId: string | null,
    includeInvoiceDescription: boolean,
  ) => Promise<{
    unknownPlaceholders: string[];
    emptyDataPlaceholders: string[];
  }>;
  /**
   * Optional list of allowed template category type codes (e.g. ["customers", "general"]).
   * When provided, only templates whose category matches one of these codes are shown.
   * Templates with no category assigned are always included.
   */
  allowedCategoryTypes?: string[];
  /**
   * Optional list of allowed template type codes (e.g. ["email"] or ["statement"]).
   * When provided, only templates whose type matches one of these codes are shown.
   */
  allowedTemplateTypes?: string[];
  /** When true, the template's `attachInvoiceCopies` preference will not be
   *  applied to the attach checkbox. Used by the Send Statements flow where
   *  the attach checkbox means "Attach statement PDF", not "Attach outstanding invoices". */
  ignoreTemplateAttachPreference?: boolean;
  /** Called after the MFE reports success with the full list of recipients and
   *  composed email details. Use this to log emails to the backend (e.g. EvoEmailerHistory). */
  onAfterSend?: (payload: {
    recipients: Array<{
      recipientId: string;
      toEmails: string[];
      ccEmails: string[];
      attachedTransactionIds?: number[];
    }>;
    subject: string;
    htmlBody: string;
    templateId?: string;
    templateName?: string;
    isSubjectModified: boolean;
    isBodyModified: boolean;
    attachOutstandingInvoices: boolean;
  }) => void;
}

type RecipientEmailMap = Record<string, { toEmails: string[]; ccEmails: string[] }>;

interface ResolvedRecipientEmails {
  toEmails: string[];
  ccEmails: string[];
  /** Primary TO address used as the recipient's `email` in the MFE payload. */
  toEmail: string;
}

function resolveRecipientEmails(
  recipient: BulkEmailRecipient,
  recipientEmails: RecipientEmailMap,
): ResolvedRecipientEmails {
  const configured = recipientEmails[recipient.id];
  const toEmails = configured?.toEmails ?? (recipient.defaultEmail ? [recipient.defaultEmail] : []);
  return { toEmails, ccEmails: configured?.ccEmails ?? [], toEmail: toEmails[0] ?? recipient.defaultEmail };
}

/** Why the composed bulk email cannot be sent, or `null` when it is valid. */
function validateBulkCompose(
  subject: string,
  activeRecipients: BulkEmailRecipient[],
  recipientEmails: RecipientEmailMap,
): { message: string; tone: 'error' | 'warning' } | null {
  const subjectMessage = disallowedSubjectVariablesMessage(subject);
  if (subjectMessage) return { message: subjectMessage, tone: 'error' };

  if (activeRecipients.length === 0) {
    return { message: 'Please select at least one recipient to send emails.', tone: 'warning' };
  }

  const withoutTo = activeRecipients.filter((r) => resolveRecipientEmails(r, recipientEmails).toEmails.length === 0);
  if (withoutTo.length > 0) {
    return {
      message: `${withoutTo.length} selected recipient(s) have no TO email address. Please add at least one TO email for each selected recipient.`,
      tone: 'warning',
    };
  }

  return null;
}

/** Message to show for a failed merge-data resolution, or `null` when it resolved cleanly. */
async function loadMergeDataErrorMessage(
  fetchMergeData: () => Promise<{ unknownPlaceholders: string[]; emptyDataPlaceholders: string[] }>,
): Promise<string | null> {
  try {
    const { unknownPlaceholders, emptyDataPlaceholders } = await fetchMergeData();
    return mergeDataPlaceholderMessage(unknownPlaceholders, emptyDataPlaceholders);
  } catch (error) {
    return error instanceof ApiError ? error.message : 'Failed to load email merge data. Please try again.';
  }
}

/** Generates attachments for every recipient, reporting each completion through `onFetched`. */
async function fetchPerRecipientAttachments(
  recipients: BulkEmailRecipient[],
  fetchAttachments: (recipient: BulkEmailRecipient) => Promise<UploadedFile[]>,
  onFetched: () => void,
): Promise<{ attachments: Map<string, UploadedFile[]>; failedCount: number }> {
  const results = await Promise.allSettled(
    recipients.map(async (r) => {
      try {
        return { id: r.id, files: await fetchAttachments(r) };
      } finally {
        onFetched();
      }
    }),
  );

  const attachments = new Map<string, UploadedFile[]>();
  for (const result of results) {
    if (result.status === 'fulfilled') attachments.set(result.value.id, result.value.files);
  }

  return { attachments, failedCount: results.length - attachments.size };
}

function buildMfeRecipient(
  recipient: BulkEmailRecipient,
  { emails, merge, files }: { emails: ResolvedRecipientEmails; merge: MergeDataResult; files: UploadedFile[] },
) {
  const mergeDataHtml = lowercaseMergeKeys(merge.mergeDataHtml ?? {});
  const overrides: Record<string, string[]> = {};
  if (emails.toEmails.length > 0) overrides.to = emails.toEmails;
  if (emails.ccEmails.length > 0) overrides.cc = emails.ccEmails;

  return {
    recipientId: recipient.id,
    name: recipient.name,
    email: emails.toEmail,
    selected: true,
    mergeData: lowercaseMergeKeys(merge.mergeData),
    ...(Object.keys(mergeDataHtml).length > 0 ? { mergeDataHtml } : {}),
    ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
    attachments: files.map((f) => ({
      type: 'base64' as const,
      filename: f.name,
      mimeType: f.contentType,
      data: f.base64Content,
    })),
  };
}

// ─── CompactEmailSelector ─────────────────────────────────────────────────────

interface CompactEmailSelectorProps {
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
  contacts?: ContactUI[];
  required?: boolean;
  placeholder?: string;
  loading?: boolean;
  maxEmails?: number;
}

export function CompactEmailSelector({
  selectedEmails,
  onEmailsChange,
  contacts = [],
  required = false,
  placeholder = 'No recipients yet',
  loading = false,
  maxEmails,
}: Readonly<CompactEmailSelectorProps>) {
  const [open, setOpen] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const handleRemove = (email: string) => {
    onEmailsChange(selectedEmails.filter((e) => e !== email));
  };

  const limitReached = maxEmails !== undefined && selectedEmails.length >= maxEmails;

  const handleAddContact = (contact: ContactUI) => {
    if (selectedEmails.includes(contact.email)) {
      onEmailsChange(selectedEmails.filter((e) => e !== contact.email));
    } else {
      if (limitReached) return;
      onEmailsChange([...selectedEmails, contact.email]);
    }
    setSearchQuery('');
  };

  const handleAddCustomEmail = () => {
    if (limitReached) return;
    const trimmed = customEmail.trim();
    if (trimmed && isValidEmail(trimmed) && !selectedEmails.includes(trimmed)) {
      onEmailsChange([...selectedEmails, trimmed]);
      setCustomEmail('');
      setShowCustomInput(false);
      setOpen(false);
    }
  };

  const getContactName = (email: string) => {
    const c = contacts.find((x) => x.email === email);
    return c ? contactDisplayName(c) : null;
  };

  const isPrimaryContact = (email: string) => contacts.find((x) => x.email === email)?.isPrimary ?? false;

  // Contacts without an email cannot be a recipient.
  const filteredContacts = contacts.filter((c) => {
    if (!c.email?.trim()) return false;
    const fullName = contactDisplayName(c).toLowerCase();
    const q = searchQuery.toLowerCase();
    return fullName.includes(q) || c.email.toLowerCase().includes(q) || c.jobTitle?.toLowerCase().includes(q);
  });

  return (
    <div className="relative w-full">
      <div className="relative border rounded-md bg-background min-h-[42px] p-2 pr-9 w-full overflow-hidden">
        {selectedEmails.length === 0 ? (
          <span className="text-xs text-muted-foreground">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1 w-full">
            {selectedEmails.map((email) => {
              const name = getContactName(email);
              const isPrimary = isPrimaryContact(email);
              return (
                <Badge
                  key={email}
                  variant={isPrimary ? 'default' : 'secondary'}
                  className={cn('pl-1.5 pr-1 py-0.5 gap-1 text-xs max-w-full', isPrimary && 'border-primary')}
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate" style={{ maxWidth: 'calc(100% - 2rem)' }}>
                    {name ? `${name} <${email}>` : email}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemove(email)}
                    className="ml-0.5 rounded-full hover:bg-muted p-0.5 shrink-0"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}

        <Popover
          open={open && !limitReached}
          onOpenChange={(v) => {
            if (v && limitReached) return;
            setOpen(v);
            if (!v) {
              setShowCustomInput(false);
              setCustomEmail('');
              setSearchQuery('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'absolute top-1.5 right-1.5 flex items-center justify-center h-6 w-6 rounded-sm bg-teal-600 hover:bg-teal-700 text-white transition-colors',
                limitReached && 'hidden',
              )}
              disabled={limitReached}
              aria-label="Add email"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search contacts…" value={searchQuery} onValueChange={setSearchQuery} />
              <CommandList>
                <CommandEmpty>No contacts found.</CommandEmpty>
                {filteredContacts.length > 0 && (
                  <CommandGroup heading="Customer Contacts">
                    <ScrollArea className="max-h-[200px]">
                      {filteredContacts.map((contact) => {
                        const isSelected = selectedEmails.includes(contact.email);
                        return (
                          <CommandItem
                            key={contact.id}
                            onSelect={() => handleAddContact(contact)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <div
                              className={cn(
                                'flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0',
                                isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                              )}
                            >
                              <Check className="h-3 w-3" />
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shrink-0">
                                <User className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate text-sm">{contactDisplayName(contact)}</span>
                                  {contact.isPrimary && (
                                    <Badge variant="outline" className="text-xs">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {contact.jobTitle && <span>{contact.jobTitle} • </span>}
                                  {contact.email}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </ScrollArea>
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandGroup>
                  {!showCustomInput ? (
                    <CommandItem onSelect={() => setShowCustomInput(true)} className="cursor-pointer">
                      <Plus className="h-4 w-4 mr-2" />
                      Add custom email address
                    </CommandItem>
                  ) : (
                    <div className="p-2 space-y-2">
                      <Input
                        ref={customInputRef}
                        type="email"
                        placeholder="email@example.com"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomEmail();
                          } else if (e.key === 'Escape') {
                            setShowCustomInput(false);
                            setCustomEmail('');
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleAddCustomEmail}
                          disabled={!customEmail.trim() || !isValidEmail(customEmail.trim())}
                        >
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCustomInput(false);
                            setCustomEmail('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {required && selectedEmails.length === 0 && !loading && (
        <p className="text-xs text-destructive mt-1">At least one recipient is required</p>
      )}
    </div>
  );
}

// ─── Per-recipient row (Step 2) ──────────────────────────────────────────────

interface RecipientRowProps {
  recipientId: string;
  accountNumber: string;
  name: string;
  toEmails: string[];
  ccEmails: string[];
  onToChange: (emails: string[]) => void;
  onCcChange: (emails: string[]) => void;
  checked: boolean;
  onToggle: () => void;
}

function RecipientRow({
  recipientId,
  accountNumber,
  name,
  toEmails,
  ccEmails,
  onToChange,
  onCcChange,
  checked,
  onToggle,
}: Readonly<RecipientRowProps>) {
  const { data: contacts = [] } = useCustomerContactsList(recipientId);

  return (
    <TableRow className={cn(!checked && 'opacity-40')}>
      <TableCell className="align-top py-3 w-[44px] max-w-[44px] overflow-hidden">
        <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Include ${name} in email run`} />
      </TableCell>
      <TableCell className="align-top py-3 w-[120px] max-w-[120px] overflow-hidden text-sm">
        <span className="block truncate">{accountNumber}</span>
      </TableCell>
      <TableCell className="align-top py-3 w-[200px] max-w-[200px] overflow-hidden text-sm">
        <span className="block truncate">{name}</span>
      </TableCell>
      <TableCell className="align-top py-2 overflow-hidden">
        <CompactEmailSelector
          selectedEmails={toEmails}
          onEmailsChange={onToChange}
          contacts={contacts}
          required={true}
          placeholder="No recipients yet"
        />
      </TableCell>
      <TableCell className="align-top py-2 overflow-hidden">
        <CompactEmailSelector
          selectedEmails={ccEmails}
          onEmailsChange={onCcChange}
          contacts={contacts}
          required={false}
          placeholder="No CC recipients"
          maxEmails={5}
        />
      </TableCell>
    </TableRow>
  );
}

// ─── Main modal (shell) ─────────────────────────────────────────────────────

export function BulkEmailModal({
  open,
  onOpenChange,
  title = 'Send Email',
  recipients,
  buildMergeData,
  callbackEventName = 'creditcontrol:bulk-email-done',
  attachLabel = 'Attach outstanding invoices',
  preparingLabel,
  fetchAttachments,
  onSuccess,
  stepOverride,
  excludedRecipientCount,
  onFetchMergeData,
  allowedCategoryTypes,
  allowedTemplateTypes,
  defaultAttachChecked,
  onAfterSend,
  ignoreTemplateAttachPreference,
}: Readonly<BulkEmailModalProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && recipients.length > 0 && (
        <BulkEmailModalContent
          onOpenChange={onOpenChange}
          title={title}
          recipients={recipients}
          buildMergeData={buildMergeData}
          callbackEventName={callbackEventName}
          attachLabel={attachLabel}
          preparingLabel={preparingLabel}
          fetchAttachments={fetchAttachments}
          onSuccess={onSuccess}
          stepOverride={stepOverride}
          excludedRecipientCount={excludedRecipientCount}
          onFetchMergeData={onFetchMergeData}
          allowedCategoryTypes={allowedCategoryTypes}
          allowedTemplateTypes={allowedTemplateTypes}
          defaultAttachChecked={defaultAttachChecked}
          onAfterSend={onAfterSend}
          ignoreTemplateAttachPreference={ignoreTemplateAttachPreference}
        />
      )}
    </Dialog>
  );
}

// ─── Modal content (unmounts on close to reset all state) ────────────────────

function BulkEmailModalContent({
  onOpenChange,
  title,
  recipients: inputRecipients,
  buildMergeData,
  callbackEventName,
  attachLabel,
  preparingLabel,
  fetchAttachments,
  onSuccess,
  stepOverride,
  excludedRecipientCount,
  onFetchMergeData,
  allowedCategoryTypes,
  allowedTemplateTypes,
  defaultAttachChecked,
  onAfterSend,
  ignoreTemplateAttachPreference,
}: Readonly<Omit<BulkEmailModalProps, 'open'>>) {
  // Compute step label helper — uses override when provided, falls back to "N of 3"
  const stepLabel = (localStep: 1 | 2 | 3) =>
    stepOverride ? `Step ${stepOverride.startAt + localStep - 1} of ${stepOverride.total}` : `Step ${localStep} of 3`;
  const modalTitle = title ?? 'Send Email';
  const eventName = callbackEventName ?? 'creditcontrol:bulk-email-done';
  const attachCheckboxLabel = attachLabel ?? 'Attach outstanding invoices';
  const excludedRecipientSuffix = excludedRecipientCount ? ` (${excludedRecipientCount} excluded)` : '';

  const { isDemo } = useMode();

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isPreparingEmails, setIsPreparingEmails] = useState(false);
  const [isPreparingAttachments, setIsPreparingAttachments] = useState(false);
  const [preparingProgress, setPreparingProgress] = useState({ done: 0, total: 0 });
  const [mfeDone, setMfeDone] = useState(false);

  // ── Auth (for MFE instanceId) ────────────────────────────────────────────
  const { instanceId } = useAuth();

  // ── Step 1 state ─────────────────────────────────────────────────────────
  const [template, setTemplate] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [subjectHtml, setSubjectHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachInvoices, setAttachInvoices] = useState(defaultAttachChecked ?? false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sentEmailDataRef = useRef<{
    recipients: Array<{ recipientId: string; to: string[]; cc: string[]; attachedTransactionIds?: number[] }>;
    subject: string;
    bodyHtml: string;
    templateId?: string;
    templateName?: string;
    isSubjectModified: boolean;
    isBodyModified: boolean;
    attachInvoices: boolean;
  } | null>(null);

  const originalTemplateSubject = useRef<string>('');
  const originalTemplateBody = useRef<string>('');
  const bodyEditorRef = useRef<Editor | null>(null);

  // ── Step 2 state ─────────────────────────────────────────────────────────
  const [recipientEmails, setRecipientEmails] = useState<Record<string, { toEmails: string[]; ccEmails: string[] }>>(
    {},
  );
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // ── Step 3 state (SendIt MFE) ────────────────────────────────────────────
  const [bulkEmailParam, setBulkEmailParam] = useState<string>('');

  // ── Data hooks ───────────────────────────────────────────────────────────
  const { data: templatesData } = useTemplatesList();
  const allTemplates = templatesData ?? [];
  const templates = allTemplates.filter((x) => {
    if (allowedCategoryTypes) {
      if (
        x.categoryTypeName &&
        !allowedCategoryTypes.some((code) => x.categoryTypeName!.toLowerCase() === code.toLowerCase())
      ) {
        return false;
      }
    }
    if (allowedTemplateTypes) {
      if (!allowedTemplateTypes.some((t) => x.type.toLowerCase() === t.toLowerCase())) {
        return false;
      }
    }
    return true;
  });
  const { data: templateDetail } = useTemplateById(selectedTemplateId);

  // ── TipTap subject editor ────────────────────────────────────────────────
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: 'Enter your subject...' }),
      VariableExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-3 min-h-[44px]',
      },
    },
    onUpdate: ({ editor }) => {
      setSubjectHtml(editor.getHTML());
    },
  });

  const handleBodyEditorReady = useCallback((editor: Editor) => {
    bodyEditorRef.current = editor;
  }, []);

  // ── Seed recipients on entering Step 2 ────────────────────────────────────
  const initRecipients = useCallback(() => {
    const init: Record<string, { toEmails: string[]; ccEmails: string[] }> = {};
    inputRecipients.forEach((r) => {
      if (!recipientEmails[r.id]) {
        init[r.id] = { toEmails: r.defaultEmail ? [r.defaultEmail] : [], ccEmails: [] };
      }
    });
    if (Object.keys(init).length > 0) {
      setRecipientEmails((prev) => ({ ...init, ...prev }));
    }
    setCheckedIds(new Set(inputRecipients.map((r) => r.id)));
  }, [inputRecipients]);

  // ── Populate editors when template is selected ────────────────────────────
  useEffect(() => {
    if (templateDetail && selectedTemplateId) {
      const rawSubject = templateDetail.subject ?? '';
      const convertedSubject = convertVariablesToNodes(rawSubject);
      const rawBody = templateDetail.content ?? '';
      const convertedBody = convertVariablesToNodes(rawBody);

      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent(convertedSubject);
        originalTemplateSubject.current = subjectEditor.getHTML();
      }
      setSubjectHtml(convertedSubject);

      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent(convertedBody);
        const rendered = bodyEditorRef.current.getHTML();
        originalTemplateBody.current = rendered;
        setBodyHtml(rendered);
      } else {
        setBodyHtml(convertedBody);
        originalTemplateBody.current = convertedBody;
      }
      if (!ignoreTemplateAttachPreference) {
        setAttachInvoices(templateDetail.attachInvoiceCopies ?? false);
      }
    }
  }, [templateDetail, selectedTemplateId, subjectEditor, ignoreTemplateAttachPreference]);

  // ── Template change ──────────────────────────────────────────────────────
  const handleTemplateChange = (value: string) => {
    setTemplate(value);
    if (value === 'none') {
      setSelectedTemplateId(null);
      setSubjectHtml('');
      originalTemplateSubject.current = '';
      originalTemplateBody.current = '';
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent('');
      }
      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent('');
      }
      setBodyHtml('');
      if (!ignoreTemplateAttachPreference) {
        setAttachInvoices(false);
      }
    } else {
      setSelectedTemplateId(value);
    }
  };

  // ── Step 1 → 2 ──────────────────────────────────────────────────────────
  const handleNext = () => {
    const currentSubjectText = convertVariableNodesToText(subjectEditor?.getHTML() || subjectHtml);
    if (!currentSubjectText.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    initRecipients();
    setStep(2);
  };

  // ── Recipient helpers ────────────────────────────────────────────────────
  const updateTo = (id: string, emails: string[]) =>
    setRecipientEmails((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { toEmails: [], ccEmails: [] }), toEmails: emails },
    }));

  const updateCc = (id: string, emails: string[]) =>
    setRecipientEmails((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { toEmails: [], ccEmails: [] }), ccEmails: emails },
    }));

  // ── Checkbox helpers ─────────────────────────────────────────────────────
  const toggleRecipient = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allChecked = inputRecipients.length > 0 && inputRecipients.every((r) => checkedIds.has(r.id));
  const someChecked = !allChecked && inputRecipients.some((r) => checkedIds.has(r.id));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(inputRecipients.map((r) => r.id)));
    }
  };

  const step2Title =
    inputRecipients.length > 1 ? `Email Recipients (${inputRecipients.length} customers)` : 'Email Recipients';

  // ── File upload handler ──────────────────────────────────────────────────
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64Content = dataUrl.split(',')[1] || '';
        setUploadedFiles((prev) => [
          ...prev,
          {
            name: file.name,
            dataUrl,
            contentType: file.type || 'application/octet-stream',
            base64Content,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleRemoveFile = (fileName: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  // ── Build bulk payload & advance to MFE step ─────────────────────────────
  // The SendIt MFE resolves <<key>> placeholders in template.subject / template.body
  // using each recipient's mergeData dictionary.
  const handleOpenMfe = async () => {
    const mfeSubject = convertVariableNodesToText(subjectEditor?.getHTML() || subjectHtml);
    const mfeBody = convertVariableNodesToPlaceholders(bodyHtml);

    const activeRecipients = inputRecipients.filter((r) => checkedIds.has(r.id));
    const validationIssue = validateBulkCompose(mfeSubject, activeRecipients, recipientEmails);
    if (validationIssue) {
      toast[validationIssue.tone](validationIssue.message);
      return;
    }

    if (isDemo) {
      toast.success(`Sent ${activeRecipients.length} email(s) successfully`);
      onSuccess?.();
      onOpenChange(false);
      return;
    }

    setIsPreparingEmails(true);

    // Phase 1: Fetch merge data from backend if an async fetcher is provided.
    // This sends subject/body so the backend only resolves required fields.
    if (onFetchMergeData) {
      const mergeDataMessage = await loadMergeDataErrorMessage(() =>
        onFetchMergeData(mfeSubject, mfeBody, selectedTemplateId, templateDetail?.includeInvoiceDescription ?? false),
      );
      if (mergeDataMessage) {
        toast.error(mergeDataMessage);
        setIsPreparingEmails(false);
        return;
      }
    }

    // If the attach checkbox is checked and a fetcher is provided, generate
    // per-recipient attachments now (e.g. statement PDFs) before opening the MFE.
    let perRecipientAttachments = new Map<string, UploadedFile[]>();
    if (attachInvoices && fetchAttachments) {
      setIsPreparingEmails(false);
      setIsPreparingAttachments(true);
      setPreparingProgress({ done: 0, total: activeRecipients.length });

      const { attachments, failedCount } = await fetchPerRecipientAttachments(activeRecipients, fetchAttachments, () =>
        setPreparingProgress((prev) => ({ ...prev, done: prev.done + 1 })),
      );
      perRecipientAttachments = attachments;

      if (failedCount > 0) {
        toast.warning(`${failedCount} attachment(s) could not be generated`);
      }

      setIsPreparingAttachments(false);
    }

    const recipientsList = activeRecipients.map((r) => {
      const emails = resolveRecipientEmails(r, recipientEmails);
      return buildMfeRecipient(r, {
        emails,
        merge: buildMergeData(r, emails.toEmail),
        files: [...uploadedFiles, ...(perRecipientAttachments.get(r.id) ?? [])],
      });
    });

    const bulkPayload = {
      template: {
        to: ['<<email>>'],
        cc_email: ['<<cc_email>>'],
        subject: mfeSubject,
        body: mfeBody,
      },
      recipients: recipientsList,
      config: { batchSize: 20, maxRetries: 2 },
    };

    // Store composed email details for the onAfterSend callback
    sentEmailDataRef.current = {
      recipients: activeRecipients.map((r) => {
        const emails = resolveRecipientEmails(r, recipientEmails);
        return {
          recipientId: r.id,
          to: emails.toEmails,
          cc: emails.ccEmails,
          attachedTransactionIds: (perRecipientAttachments.get(r.id) ?? [])
            .map((file) => file.transactionId)
            .filter((id): id is number => id !== undefined),
        };
      }),
      subject: mfeSubject,
      bodyHtml: mfeBody,
      templateId: selectedTemplateId ?? undefined,
      templateName: templateDetail?.name,
      isSubjectModified: subjectEditor ? subjectEditor.getHTML() !== originalTemplateSubject.current : false,
      isBodyModified: bodyHtml !== originalTemplateBody.current,
      attachInvoices: attachInvoices,
    };

    setIsPreparingEmails(false);
    setBulkEmailParam(JSON.stringify(bulkPayload));
    setStep(3);
  };

  // Stable MFE parameters to avoid re-renders
  const mfeParameters = useMemo(
    () =>
      bulkEmailParam
        ? {
            mode: 'bulk' as const,
            bulkEmail: bulkEmailParam,
            callbackEventName: eventName,
          }
        : undefined,
    [bulkEmailParam, eventName],
  );

  // ── Listen for SendIt MFE callback ────────────────────────────────────
  useEffect(() => {
    if (step !== 3) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      // MFE Cancel / Back button — just close the dialog
      if (detail?.action === 'close') {
        onOpenChange(false);
        return;
      }

      // MFE success — hide MFE content immediately to prevent flash of its
      // own "Compose New / Close" screen during the dialog close animation.
      setMfeDone(true);
      const { bulkResults } = detail as {
        bulkResults: { successCount: number; totalRecipients: number };
      };
      toast.success(`Sent ${bulkResults.successCount}/${bulkResults.totalRecipients} emails`);
      if (onAfterSend && sentEmailDataRef.current) {
        const {
          recipients,
          subject,
          bodyHtml,
          templateId,
          templateName,
          isSubjectModified,
          isBodyModified,
          attachInvoices: attachInv,
        } = sentEmailDataRef.current;
        onAfterSend({
          recipients: recipients.map((r) => ({
            recipientId: r.recipientId,
            toEmails: r.to,
            ccEmails: r.cc,
            attachedTransactionIds: r.attachedTransactionIds,
          })),
          subject,
          htmlBody: bodyHtml,
          templateId,
          templateName,
          isSubjectModified,
          isBodyModified,
          attachOutstandingInvoices: attachInv,
        });
      }
      onSuccess?.();
      onOpenChange(false);
    };

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [step]);

  // ── Step 1 render ────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogDescription>
            {stepLabel(1)} — Compose email for {inputRecipients.length} customer
            {inputRecipients.length !== 1 ? 's' : ''}
            {excludedRecipientSuffix}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2 overflow-y-auto flex-1 min-h-0">
          {/* Template */}
          <div className="grid gap-2">
            <Label htmlFor="bulk-template">Template (Optional)</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger id="bulk-template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="grid gap-2">
            <Label>Subject</Label>
            {subjectEditor ? (
              <div className="border rounded-md">
                <EditorContent
                  editor={subjectEditor}
                  className="prose prose-sm max-w-none [&_.ProseMirror]:p-3 [&_.ProseMirror]:min-h-[44px] [&_.ProseMirror]:focus:outline-none"
                />
              </div>
            ) : (
              <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">Loading editor…</div>
            )}
          </div>

          {/* Message */}
          <div className="grid gap-2">
            <Label>Message</Label>
            <RichTextEditor
              content={bodyHtml}
              onChange={setBodyHtml}
              onEditorReady={handleBodyEditorReady}
              placeholder="Enter your message…"
            />
          </div>

          {/* Attach invoices */}
          {fetchAttachments && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="bulk-attach-invoices"
                checked={attachInvoices}
                onCheckedChange={(checked) => setAttachInvoices(checked === true)}
              />
              <Label htmlFor="bulk-attach-invoices" className="text-sm font-normal">
                {attachCheckboxLabel}
              </Label>
            </div>
          )}

          {/* File upload */}
          <div className="grid gap-2">
            <Label className="text-sm font-normal">Attach files</Label>
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Paperclip className="mr-2 h-4 w-4" />
                Choose files
              </Button>
              {uploadedFiles.length > 0 && (
                <span className="text-xs text-muted-foreground">{uploadedFiles.length} file(s) selected</span>
              )}
              <span className="text-xs text-muted-foreground italic">
                Any files uploaded with &apos;Choose Files&apos; button, will be sent to all selected customers
              </span>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {uploadedFiles.map((f) => (
                  <Badge key={f.name} variant="secondary" className="pl-2 pr-1 py-0.5 gap-1 text-xs">
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[200px]">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(f.name)}
                      className="ml-0.5 rounded-full hover:bg-muted p-0.5 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  // ── Step 2 render ────────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <DialogContent className="sm:max-w-[1200px] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{step2Title}</DialogTitle>
          <DialogDescription>{stepLabel(2)} — Review and configure recipients for each customer</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border">
          <div className="shrink-0 border-b bg-muted/50">
            <Table className="table-fixed w-full">
              <colgroup>
                <col className="w-[44px]" />
                <col className="w-[120px]" />
                <col className="w-[200px]" />
                <col />
                <col />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[44px]">
                    <Checkbox
                      checked={allChecked}
                      ref={(el) => {
                        if (el) (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someChecked;
                      }}
                      onCheckedChange={toggleAll}
                      aria-label="Select all customers"
                    />
                  </TableHead>
                  <TableHead className="w-[120px]">Account</TableHead>
                  <TableHead className="w-[200px]">Customer</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>CC</TableHead>
                </TableRow>
              </TableHeader>
            </Table>
          </div>
          <div className="overflow-y-auto flex-1">
            <Table className="table-fixed w-full">
              <colgroup>
                <col className="w-[44px]" />
                <col className="w-[120px]" />
                <col className="w-[200px]" />
                <col />
                <col />
              </colgroup>
              <TableBody>
                {inputRecipients.map((r) => (
                  <RecipientRow
                    key={r.id}
                    recipientId={r.id}
                    accountNumber={r.accountNumber}
                    name={r.name}
                    toEmails={recipientEmails[r.id]?.toEmails ?? (r.defaultEmail ? [r.defaultEmail] : [])}
                    ccEmails={recipientEmails[r.id]?.ccEmails ?? []}
                    onToChange={(emails) => updateTo(r.id, emails)}
                    onCcChange={(emails) => updateCc(r.id, emails)}
                    checked={checkedIds.has(r.id)}
                    onToggle={() => toggleRecipient(r.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setStep(1)} className="mr-auto">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleOpenMfe}
            disabled={isPreparingEmails || isPreparingAttachments}
            className="min-w-[16rem] whitespace-nowrap"
          >
            {isPreparingEmails ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparing emails…
              </>
            ) : isPreparingAttachments ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {preparingLabel ?? 'Loading Customer Invoices'} {preparingProgress.done}/{preparingProgress.total}…
              </>
            ) : (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  // ── Step 3 render (SendIt MFE) ───────────────────────────────────────────
  // Render only the MFE without our own header/footer — the SendIt MFE
  // provides its own stepper, Cancel, and Back controls.
  return (
    <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto">
        {mfeParameters && !mfeDone && (
          <EvoMfeApp name="evo-platform-sendit-mfe" instanceId={instanceId} parameters={mfeParameters} />
        )}
      </div>
    </DialogContent>
  );
}
